/**
 * Utilities for text selection and highlighting in document viewers.
 * Used by DocxViewer and TxtViewer for inline text proofing.
 */

/**
 * Compute the character offset of a position within a container by walking
 * all text nodes in document order.
 *
 * @param {Node} node - The node containing the position
 * @param {number} offset - The offset within that node
 * @param {HTMLElement} container - The root container to measure from
 * @returns {number} Character offset from the start of container's text content
 */
export function getCharacterOffset(node, offset, container) {
  const walker = document.createTreeWalker(container, NodeFilter.SHOW_TEXT);
  let charCount = 0;

  while (walker.nextNode()) {
    if (walker.currentNode === node) {
      return charCount + offset;
    }
    charCount += walker.currentNode.textContent.length;
  }

  return charCount + offset;
}

/**
 * Find the text node and local offset for a given global character offset.
 *
 * @param {HTMLElement} container - The root container
 * @param {number} targetOffset - Global character offset
 * @returns {{ node: Node, offset: number } | null}
 */
export function findNodeAtOffset(container, targetOffset) {
  const walker = document.createTreeWalker(container, NodeFilter.SHOW_TEXT);
  let charCount = 0;

  while (walker.nextNode()) {
    const nodeLength = walker.currentNode.textContent.length;
    if (charCount + nodeLength >= targetOffset) {
      return {
        node: walker.currentNode,
        offset: targetOffset - charCount,
      };
    }
    charCount += nodeLength;
  }

  return null;
}

/**
 * Capture the current text selection within a container and return
 * selection metadata suitable for creating a text annotation.
 *
 * @param {HTMLElement} container - The document viewer container
 * @returns {{ selectedText: string, textSelectionStart: number, textSelectionEnd: number } | null}
 */
export function captureTextSelection(container) {
  const selection = window.getSelection();
  if (!selection || selection.isCollapsed || selection.rangeCount === 0) {
    return null;
  }

  const range = selection.getRangeAt(0);

  // Ensure selection is within the container
  if (!container.contains(range.startContainer) || !container.contains(range.endContainer)) {
    return null;
  }

  const selectedText = selection.toString().trim();
  if (!selectedText) {
    return null;
  }

  const textSelectionStart = getCharacterOffset(range.startContainer, range.startOffset, container);
  const textSelectionEnd = getCharacterOffset(range.endContainer, range.endOffset, container);

  return {
    selectedText,
    textSelectionStart,
    textSelectionEnd,
  };
}

/**
 * Apply highlights to a container for a set of text annotations.
 * Each annotation gets <mark> elements wrapping the selected text range.
 *
 * @param {HTMLElement} container - The document viewer container
 * @param {Array} annotations - Array of annotation objects with textSelectionStart, textSelectionEnd, selectedText, id
 * @param {Object} usersColor - Map of userId/approverId to color
 * @param {Function} onHighlightClick - Callback when a highlight is clicked, receives annotation id
 */
export function applyTextHighlights(container, annotations, usersColor, onHighlightClick) {
  // Remove existing highlights
  clearTextHighlights(container);

  if (!annotations || annotations.length === 0) return;

  // Sort annotations by start offset descending so we can apply from end to start
  // without invalidating earlier offsets
  const sorted = [...annotations]
    .filter(a => a.type === 'text' && a.textSelectionStart != null && a.textSelectionEnd != null)
    .sort((a, b) => b.textSelectionStart - a.textSelectionStart);

  for (const annotation of sorted) {
    try {
      highlightRange(container, annotation, usersColor, onHighlightClick);
    } catch (e) {
      console.warn('Failed to apply highlight for annotation', annotation.id, e);
    }
  }
}

/**
 * Highlight a single annotation's text range in the container.
 */
function highlightRange(container, annotation, usersColor, onHighlightClick) {
  let startInfo = findNodeAtOffset(container, annotation.textSelectionStart);
  let endInfo = findNodeAtOffset(container, annotation.textSelectionEnd);

  // Fallback: if offsets don't match the expected text, search for the text
  if (startInfo && endInfo) {
    const textContent = container.textContent;
    const actualText = textContent.substring(annotation.textSelectionStart, annotation.textSelectionEnd);
    if (actualText !== annotation.selectedText && annotation.selectedText) {
      const fallbackIndex = textContent.indexOf(annotation.selectedText);
      if (fallbackIndex !== -1) {
        startInfo = findNodeAtOffset(container, fallbackIndex);
        endInfo = findNodeAtOffset(container, fallbackIndex + annotation.selectedText.length);
      }
    }
  }

  if (!startInfo || !endInfo) return;

  const range = document.createRange();
  range.setStart(startInfo.node, startInfo.offset);
  range.setEnd(endInfo.node, endInfo.offset);

  const colorId = annotation.userId || annotation.approverId;
  const color = (usersColor && usersColor[colorId]) || '#FBBF24';

  // Wrap the range contents in mark elements
  const textNodes = getTextNodesInRange(range);
  for (const { node, start, end } of textNodes) {
    const mark = document.createElement('mark');
    mark.className = 'annotation-highlight';
    mark.dataset.annotationId = String(annotation.id);
    mark.style.backgroundColor = color + '33'; // 20% opacity
    mark.style.borderBottom = `2px solid ${color}`;
    mark.style.cursor = 'pointer';
    mark.style.borderRadius = '2px';

    if (onHighlightClick) {
      mark.addEventListener('click', (e) => {
        e.stopPropagation();
        onHighlightClick(annotation.id);
      });
    }

    // Split text node if needed and wrap
    const textNode = node;
    if (start > 0) {
      textNode.splitText(start);
      const newNode = textNode.nextSibling;
      if (end - start < newNode.textContent.length) {
        newNode.splitText(end - start);
      }
      newNode.parentNode.insertBefore(mark, newNode);
      mark.appendChild(newNode);
    } else if (end < textNode.textContent.length) {
      textNode.splitText(end);
      textNode.parentNode.insertBefore(mark, textNode);
      mark.appendChild(textNode);
    } else {
      textNode.parentNode.insertBefore(mark, textNode);
      mark.appendChild(textNode);
    }
  }
}

/**
 * Get all text nodes within a range, with their local start/end offsets.
 */
function getTextNodesInRange(range) {
  const result = [];
  const walker = document.createTreeWalker(
    range.commonAncestorContainer.nodeType === Node.TEXT_NODE
      ? range.commonAncestorContainer.parentNode
      : range.commonAncestorContainer,
    NodeFilter.SHOW_TEXT
  );

  let inRange = false;
  while (walker.nextNode()) {
    const node = walker.currentNode;

    if (node === range.startContainer) {
      inRange = true;
      const start = range.startOffset;
      const end = node === range.endContainer ? range.endOffset : node.textContent.length;
      result.push({ node, start, end });
      if (node === range.endContainer) break;
      continue;
    }

    if (node === range.endContainer) {
      result.push({ node, start: 0, end: range.endOffset });
      break;
    }

    if (inRange) {
      result.push({ node, start: 0, end: node.textContent.length });
    }
  }

  // Handle case where start and end are the same node
  if (result.length === 0 && range.startContainer === range.endContainer && range.startContainer.nodeType === Node.TEXT_NODE) {
    result.push({
      node: range.startContainer,
      start: range.startOffset,
      end: range.endOffset,
    });
  }

  return result;
}

/**
 * Remove all highlights from a container.
 */
export function clearTextHighlights(container) {
  const marks = container.querySelectorAll('mark.annotation-highlight');
  marks.forEach(mark => {
    const parent = mark.parentNode;
    while (mark.firstChild) {
      parent.insertBefore(mark.firstChild, mark);
    }
    parent.removeChild(mark);
    parent.normalize(); // Merge adjacent text nodes
  });
}

/**
 * Scroll to and pulse a specific annotation highlight.
 *
 * @param {HTMLElement} container - The document viewer container
 * @param {number|string} annotationId - The annotation ID to scroll to
 */
export function scrollToHighlight(container, annotationId) {
  const mark = container.querySelector(`mark.annotation-highlight[data-annotation-id="${annotationId}"]`);
  if (!mark) return;

  mark.scrollIntoView({ behavior: 'smooth', block: 'center' });

  // Pulse animation
  mark.style.transition = 'background-color 0.3s';
  const originalBg = mark.style.backgroundColor;
  mark.style.backgroundColor = '#FBBF2488';
  setTimeout(() => {
    mark.style.backgroundColor = originalBg;
  }, 1000);
}
